import { useState, useEffect, useCallback } from 'react';
import { taskService } from '../../../services/quantifiedTaskService';
import { queueHomeRequest } from '../homeRequestQueue';

export interface UseKPITaskCountsResult {
  counts: Record<string, number>;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export interface HomeAreaFilterInput {
  serviceAreaName?: string;
  foremanAreaNames?: string[];
}

/**
 * Fetches total task counts per status (and optionally scoped by service/foreman area).
 * Uses getQuantifiedTasksPaginated with limit: 1 to get pagination.totalCount per status.
 * When filter is provided, only those areas are used; when absent/empty, no area filter is applied.
 */
export function useKPITaskCounts(
  statuses: string[],
  filter?: HomeAreaFilterInput | null
): UseKPITaskCountsResult {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const foremanAreaNames = filter?.foremanAreaNames;
  const serviceAreaName = filter?.serviceAreaName;
  const hasForemanFilter = foremanAreaNames && foremanAreaNames.length > 0;
  const hasServiceFilter = !!serviceAreaName;

  const fetchCounts = useCallback(async () => {
    if (statuses.length === 0) {
      setCounts({});
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const results = await Promise.all(
        statuses.map(async (status) => {
          const params: Parameters<typeof taskService.getQuantifiedTasksPaginated>[0] = {
            status,
            limit: 1,
          };
          if (hasForemanFilter) {
            params.foremanArea = foremanAreaNames;
          }
          if (hasServiceFilter && !hasForemanFilter) {
            params.serviceArea = serviceAreaName;
          }
          const response = await queueHomeRequest(() => taskService.getQuantifiedTasksPaginated(params));
          return { status, totalCount: response.pagination?.totalCount ?? 0 };
        })
      );

      const nextCounts: Record<string, number> = {};
      results.forEach(({ status, totalCount }) => {
        nextCounts[status] = totalCount;
      });
      setCounts(nextCounts);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Failed to fetch task counts')
      );
      setCounts({});
    } finally {
      setLoading(false);
    }
  }, [
    statuses.join(','),
    hasForemanFilter ? foremanAreaNames!.join(',') : '',
    hasServiceFilter ? serviceAreaName : '',
  ]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  return { counts, loading, error, refetch: fetchCounts };
}
